﻿import {defineStore, storeToRefs} from 'pinia';
import {bootstrap, type GlobalHeaderClient, type IDPData} from "@govflanders/vl-widget-global-header-types";
import {bootstrap as globalFooterBootstrap, type GlobalFooterClient} from "@govflanders/vl-widget-global-footer-types";
import {ref, type Ref} from 'vue';
import {useUserStore} from "@/app/stores/user-store.ts";

const settings = {
    acmHeaderId: import.meta.env.VITE_GLOBAL_HEADER_ACM_WIDGET_ID,
    acmHeaderUrl: import.meta.env.VITE_GLOBAL_HEADER_ACM_WIDGET_URL,
    acmFooterId: import.meta.env.VITE_GLOBAL_FOOTER_ACM_WIDGET_ID,
    acmFooterUrl: import.meta.env.VITE_GLOBAL_FOOTER_ACM_WIDGET_URL,
    loginUrl: "/auth/login",
    logoutUrl: "/auth/logout",
}

export const useGlobalHeaderStore = defineStore('global-header-store', () => {
    const globalHeaderClient: Ref<GlobalHeaderClient | null> = ref(null);
    const globalFooterClient: Ref<GlobalFooterClient | null> = ref(null);
    const isMounted = ref(false);
    const headerUrl = `${settings.acmHeaderUrl}/api/v2/widget/${settings.acmHeaderId}/entry`;
    const footerUrl = `${settings.acmFooterUrl}/api/v2/widget/${settings.acmFooterId}/entry`;
    const userStore = useUserStore();

    const {user, isAuthenticated} = storeToRefs(userStore);
    
    const ensureHeaderClient = async (): Promise<GlobalHeaderClient> => {
        try {
            if (!globalHeaderClient.value) {
                globalHeaderClient.value = await bootstrap(headerUrl);
            }
            return globalHeaderClient.value;
        } catch (error) {
            throw new Error(`Failed to initialize global header client: ${error}`);
        }
    };

    const ensureFooterClient = async (): Promise<GlobalFooterClient> => {
        try {
            if (!globalFooterClient.value) {
                globalFooterClient.value = await globalFooterBootstrap(footerUrl);
            }
            return globalFooterClient.value;
        } catch (error) {
            throw new Error(`Failed to initialize global footer client: ${error}`);
        }
    };

    const createIdpData = (): IDPData | undefined => {
        if (!isAuthenticated.value) return undefined;

        return {
            user: {
                firstName: user.value.actorInfo?.name ?? '',
                name: user.value.actorInfo?.lastname ?? ''
            }
        };
    };

    const setup = async (headerMountElement: HTMLElement, footerMountElement: HTMLElement): Promise<void> => {
        if (isMounted.value) return;

        try {
            const headerClient = await ensureHeaderClient();
            await headerClient.mount(headerMountElement);
            await headerClient.setNavigationEnabled(true);
            await syncCitizenProfile();
            await setHeaderBranding();
            
            const footerClient = await ensureFooterClient();
            await footerClient.mount(footerMountElement);
            isMounted.value = true;
        } catch (error) {
            throw new Error(`Failed to setup global header: ${error}`);
        }
    };

    const destroy = async (): Promise<void> => {
        if (!isMounted.value) return;

        try {
            const headerClient = await ensureHeaderClient();
            await headerClient.unmount();
            
            const footerClient = await ensureFooterClient();
            await footerClient.unmount();
            
            isMounted.value = false;
        } catch (error) {
            throw new Error(`Failed to destroy global header: ${error}`);
        }
    };

    const syncCitizenProfile = async (): Promise<void> => {
        if (!isAuthenticated.value) return;

        try {
            const headerClient = await ensureHeaderClient();
            await headerClient.accessMenu.setProfile({
                active: isAuthenticated.value,
                loginUrl: settings.loginUrl,
                logoutUrl: settings.logoutUrl,
                idpData: createIdpData()
            });
        } catch (error) {
            throw new Error(`Failed to sync citizen profile: ${error}`);
        }
    };

    const setHeaderBranding = async (): Promise<void> => {
        const headerClient = await ensureHeaderClient();
        await headerClient.branding.setLevel(3);
        await headerClient.branding.setColors({ primary: '#210040', functional: '#2E109E' });
    }
    
    return {
        isMounted,
        setup,
        destroy,
        syncCitizenProfile
    };
});